import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContratos } from '@hooks/useContratos.js'
import { useContatos } from '@hooks/useContatos.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { NavIcon } from '@components/layout/NavIcons.js'
import { Button } from '@components/primitives/Button.js'
import { EmptyPanel } from '@components/primitives/EmptyPanel.js'
import { Money } from '@components/primitives/Money.js'
import { CardListSkeleton, PageHeaderSkeleton } from '@components/primitives/Skeleton.js'
import { ContratoCard } from '@components/contratos/ContratoCard.js'
import { ContratoFormModal } from '@components/contratos/ContratoFormModal.js'
import { ContatoFormModal } from '@components/contatos/ContatoFormModal.js'
import { contratoDetailPath } from '@utils/constants.js'

type Filtro = 'ativos' | 'todos' | 'concluidos'

export function ContratosPage() {
  const navigate = useNavigate()
  const { itens, totais, isLoading, criar } = useContratos()
  const { todos: contatos, criar: criarContato } = useContatos()
  const [filtro, setFiltro] = useState<Filtro>('ativos')
  const [modalOpen, setModalOpen] = useState(false)
  const [contatoModalOpen, setContatoModalOpen] = useState(false)

  const visiveis = useMemo(() => {
    if (filtro === 'todos') return itens
    if (filtro === 'concluidos') return itens.filter((c) => c.status !== 'ativo')
    return itens.filter((c) => c.status === 'ativo')
  }, [itens, filtro])

  const abrir = useCallback((id: number) => navigate(contratoDetailPath(id)), [navigate])

  if (isLoading) {
    return (
      <PageShell>
        <PageHeaderSkeleton label='Carregando contratos…' />
        <section className='cf3-block'>
          <CardListSkeleton count={2} label='Carregando contratos…' />
        </section>
      </PageShell>
    )
  }

  const temContratos = itens.length > 0

  return (
    <PageShell>
      <ModePageHeader
        title='Contratos e pagamentos'
        subtitle='Cada serviço com seu valor, marcos e o que já foi pago. Pague por medição, não por promessa.'
      >
        <Button variant='primary' size='sm' onClick={() => setModalOpen(true)}>
          Novo contrato
        </Button>
      </ModePageHeader>

      {!temContratos ? (
        <>
          <EmptyPanel
            icon={<NavIcon icon='contratos' size={24} />}
            title='Nenhum contrato registrado'
            description='Registre o que combinou com pedreiro, empreiteiro, eletricista e outros: valor total, sinal, marcos de medição e retenção final. O app avisa quando um pagamento vence e quando você está pagando antes do serviço.'
            actionLabel='Registrar primeiro contrato'
            onAction={() => setModalOpen(true)}
          />
          <section className='cf3-block' aria-label='Regras de ouro'>
            <h2 className='cf3-subsection-title'>Três regras que protegem seu dinheiro</h2>
            <ul className='cf3-regras'>
              <li className='cf3-regras__item'>
                <strong>Sinal de 10 a 15%.</strong> Nunca acima de 30% antes de começar.
              </li>
              <li className='cf3-regras__item'>
                <strong>Pague por marco medido.</strong> Laje concretada, parede levantada, não
                "sexta-feira".
              </li>
              <li className='cf3-regras__item'>
                <strong>Retenha 10% até a vistoria final.</strong> É a garantia de que os arremates
                serão feitos.
              </li>
            </ul>
          </section>
        </>
      ) : (
        <>
          <section className='cf3-block' aria-label='Totais dos contratos'>
            <div className='cf3-gestao-kpis'>
              <div className='cf3-gestao-kpi'>
                <span className='cf3-gestao-kpi__label'>Contratado</span>
                <Money value={totais.contratado} size={20} />
              </div>
              <div className='cf3-gestao-kpi'>
                <span className='cf3-gestao-kpi__label'>Pago</span>
                <Money value={totais.pago} size={20} color='var(--cf-color-success-700)' />
              </div>
              <div className='cf3-gestao-kpi'>
                <span className='cf3-gestao-kpi__label'>A pagar</span>
                <Money value={totais.aPagar} size={20} />
              </div>
            </div>
            {totais.vencidas > 0 && (
              <p className='cf3-gestao-aviso cf3-gestao-aviso--danger' role='status'>
                {totais.vencidas === 1
                  ? '1 parcela com data prevista vencida'
                  : `${totais.vencidas} parcelas com data prevista vencida`}
              </p>
            )}
          </section>

          <section className='cf3-block'>
            <div
              className='cf3-segment cf3-segment--full'
              role='tablist'
              aria-label='Filtrar contratos'
            >
              {(
                [
                  ['ativos', `Em andamento (${totais.ativos})`],
                  ['concluidos', 'Concluídos'],
                  ['todos', 'Todos'],
                ] as [Filtro, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type='button'
                  role='tab'
                  aria-selected={filtro === value}
                  className={`cf3-segment__btn${filtro === value ? ' cf3-segment__btn--active' : ''}`}
                  onClick={() => setFiltro(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            {visiveis.length === 0 ? (
              <EmptyPanel
                title='Nada por aqui'
                description={
                  filtro === 'ativos'
                    ? 'Todos os contratos estão concluídos.'
                    : 'Nenhum contrato concluído ou cancelado ainda.'
                }
              />
            ) : (
              <div className='cf3-gestao-list'>
                {visiveis.map((c) => (
                  <ContratoCard key={c.id} item={c} onOpen={abrir} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <ContratoFormModal
        open={modalOpen}
        contatos={contatos}
        onClose={() => setModalOpen(false)}
        onSalvar={async (input, parcelas) => {
          const id = await criar(input, parcelas)
          navigate(contratoDetailPath(id))
        }}
        onNovoContato={() => setContatoModalOpen(true)}
      />
      <ContatoFormModal
        open={contatoModalOpen}
        papelInicial='empreiteiro'
        onClose={() => setContatoModalOpen(false)}
        onSalvar={criarContato}
      />
    </PageShell>
  )
}
